import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Radar, Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/ui/password-input";
import { PhoneInput } from "@/components/ui/phone-input";
import { validateEmail, validatePhone } from "@/lib/validation";
import { useTranslation } from "react-i18next";
import { API_BASE_URL } from "@/lib/api-base";

const API_BASE = API_BASE_URL;

export default function SignupPage() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    phone: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  function update(field: string, value: string) {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear field error on change
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  }

  function handleEmailBlur() {
    if (form.email) {
      setFieldErrors((prev) => ({
        ...prev,
        email: validateEmail(form.email),
      }));
    }
  }

  function handlePhoneBlur() {
    if (form.phone) {
      setFieldErrors((prev) => ({
        ...prev,
        phone: validatePhone(form.phone),
      }));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Run all validations on submit
    const emailErr = validateEmail(form.email);
    const phoneErr = form.phone ? validatePhone(form.phone) : null;
    if (emailErr || phoneErr) {
      setFieldErrors({ email: emailErr, phone: phoneErr });
      return;
    }

    if (form.password.length < 8) {
      setError(t("signup.error_passwordTooShort"));
      return;
    }

    setLoading(true);

    try {
      const body: Record<string, string> = {
        email: form.email,
        password: form.password,
        first_name: form.first_name,
        last_name: form.last_name,
      };
      if (form.phone) body.phone = form.phone;
      if (inviteToken) body.invite_token = inviteToken;

      const res = await fetch(`${API_BASE}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(
          data?.detail ||
            `${t("signup.error_registrationFailed")} (${res.status})`,
        );
      }

      navigate("/verify-email", { state: { email: form.email } });
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("signup.error_registrationFailed"),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md shadow-card">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
            <Radar className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">{t("signup.title")}</CardTitle>
          <CardDescription>
            {inviteToken
              ? t("signup.descriptionInvited")
              : t("signup.descriptionDefault")}
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">{t("signup.firstNameLabel")}</Label>
                <Input
                  id="first_name"
                  placeholder={t("signup.firstNamePlaceholder")}
                  value={form.first_name}
                  onChange={(e) => update("first_name", e.target.value)}
                  required
                  autoComplete="given-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">{t("signup.lastNameLabel")}</Label>
                <Input
                  id="last_name"
                  placeholder={t("signup.lastNamePlaceholder")}
                  value={form.last_name}
                  onChange={(e) => update("last_name", e.target.value)}
                  required
                  autoComplete="family-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t("signup.emailLabel")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("signup.emailPlaceholder")}
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                onBlur={handleEmailBlur}
                required
                autoComplete="email"
                className={fieldErrors.email ? "border-destructive" : ""}
              />
              {fieldErrors.email && (
                <p className="text-sm text-destructive">{fieldErrors.email}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("signup.passwordLabel")}</Label>
              <PasswordInput
                id="password"
                placeholder={t("signup.passwordPlaceholder")}
                value={form.password}
                onChange={(e) => update("password", e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">{t("signup.phoneLabel")}</Label>
              <PhoneInput
                id="phone"
                value={form.phone}
                onChange={(value) => update("phone", value)}
                onBlur={handlePhoneBlur}
                error={fieldErrors.phone ?? undefined}
                autoComplete="tel"
              />
            </div>
          </CardContent>
          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t("signup.submitButton")}
            </Button>
            <p className="text-sm text-muted-foreground">
              {t("signup.hasAccount")}{" "}
              <Link to="/login" className="text-primary hover:underline">
                {t("signup.loginLink")}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
